import { useEffect, useRef, useState } from 'react'
import { Counter, Tag } from '../badgesAndTags'
import Filter from '../inputs/filter'
import Loader from '../loaders/loader'
import {
	TypographyCaps,
	TypographyHeading,
	TypographyParagraph,
} from '../typography'
import Toast from '../toastsAndMessages/toast'
import { CopyIcon, MoreIcon, OpenInNewIcon, PlusIcon } from '../icons'
import AttestationsTable from './attestationsTable.tsx'
import {
	isRouteErrorResponse,
	useFetcher,
	useNavigate,
	useRouteError,
	useSearchParams,
} from '@remix-run/react'
import { SignModal } from '../common/signModal.tsx'
import {
	useIsLoggedIn,
	DynamicContext,
	type UserProfile,
	useRefreshUser,
} from '@dynamic-labs/sdk-react-core'
import { useDynamicContext } from '@dynamic-labs/sdk-react-core'
import { DeleteModal } from './deleteModal.tsx'
import { toast } from 'sonner'
import {
	AttestationType,
	nft,
	nftCollectionInfo,
	PinnedMetadataProperties,
	TargetWallet,
} from '#types/loreTypes.tsx'
import PillTab from '../tabs/pillTab.tsx'
import Button from '../buttons/button.tsx'
import Tabs from '../tabs/tabs.tsx'
import ImageInput from '../inputs/imageInput.tsx'
import InputField from '../inputs/inputField.tsx'
import { captureRemixErrorBoundaryError } from '@sentry/remix'
import FallbackAvatar from '#images/avatar11white.png'
import useAnalytics from '#app/utils/analytics/useAnalytics'
import { type Theme, useTheme } from '#app/utils/providers/themeProvider.js'
import Skeleton from 'react-loading-skeleton'
import { getNFTCollectionInfo, getUserProfileURL } from '#app/utils/misc.tsx'
import { Tooltip, TooltipRefProps } from 'react-tooltip'
import InternalLink from '../common/internalLink.tsx'

type SignParamsType = {
	open: boolean
	onClose?: () => void
	template?: string
	pendingSign?: PinnedMetadataProperties
	countersignEntry?: AttestationType
	matchingAddress?: boolean
	isValidSign?: boolean
	nft?: nft
	nftCollectionInfo?: nftCollectionInfo
	contractAddress?: string
	tokenId?: string
	origin?: string
	atomicLoreUrl?: string
	targetWallet?: TargetWallet | null
}

export default function UserProfile({
	userId,
	origin,
	signModalParams,
	atomicLoreUrl,
	params,
}: {
	userId?: string
	origin: string
	atomicLoreUrl: string
	signModalParams?: {
		isOpen: boolean
		templateFromURL: string
		cidMetadata: AttestationType
		matchingAddress: boolean
		isValidSign: boolean
	}
	params: string | undefined
}) {
	const [theme] = useTheme()
	const [searchParams, setSearchParams] = useSearchParams()
	const [attestations, setAttestations] = useState({
		completedAttestations: [],
		totalCompletedAttestations: 0,
		pendingAttestations: [],
		totalPendingAttestations: 0,
		draftAttestations: [],
		totalDraftAttestations: 0,
	})
	const [hasLoadedAttestations, setHasLoadedAttestations] = useState(false)
	const [refreshList, setRefreshList] = useState(false)
	const [signParams, setSignParams] = useState<SignParamsType>({ open: false })

	const [entryToDelete, setEntryToDelete] =
		useState<PinnedMetadataProperties | null>(null)
	const [currentTab, setCurrentTab] = useState<
		'edit' | 'signatures' | undefined
	>(undefined)

	const tabFromParams = searchParams.get('view')
	const tabOptionFromParams = searchParams.get('selection')
	const [currentTabOption, setCurrentTabOption] = useState<
		'completed' | 'drafts' | 'pending' | undefined
	>(undefined)

	useEffect(() => {
		if (
			!tabFromParams ||
			(tabFromParams !== 'edit' && tabFromParams !== 'signatures')
		) {
			setSearchParams(prev => {
				prev.set('view', 'edit')
				return prev
			})
		}
		if (
			tabOptionFromParams &&
			tabOptionFromParams !== currentTabOption &&
			[undefined, 'completed', 'drafts', 'pending'].includes(
				tabOptionFromParams,
			)
		) {
			setCurrentTabOption(tabOptionFromParams)
		}
	}, [])

	useEffect(() => {
		if (
			tabFromParams &&
			(tabFromParams === 'edit' || tabFromParams === 'signatures')
		) {
			if (currentTab !== tabFromParams) {
				if (tabFromParams === 'edit') {
					setCurrentTabOption(undefined)
				}
				setCurrentTab(tabFromParams)
			}
		}
	}, [currentTab, tabFromParams])

	const handleTabChange = (v: 'edit' | 'signatures') => {
		setCurrentTab(v)
		setSearchParams(prev => {
			prev.set('view', v)
			if (v === 'edit') {
				if (prev.get('selection')) {
					prev.delete('selection')
				}
			}
			return prev
		})
	}

	const handleTabOptionChange = (
		v: 'completed' | 'pending' | 'drafts' | undefined,
	) => {
		setCurrentTabOption(v)
		setSearchParams(prev => {
			if (v) {
				prev.set('selection', v)
			} else {
				prev.delete('selection')
			}
			return prev
		})
	}

	return (
		<div className="mx-auto max-w-[1440px] px-4 py-16 md:px-12 lg:px-36">
			{DynamicContext && DynamicContext._currentValue ? (
				<UserProfileHeader
					theme={theme}
					currentTab={currentTab}
					currentTabOption={currentTabOption}
					onChange={(v: 'edit' | 'signatures') => handleTabChange(v)}
					onChangeTabOption={(
						v: 'completed' | 'drafts' | 'pending' | undefined,
					) => handleTabOptionChange(v)}
					searchParams={searchParams}
				/>
			) : userId ? (
				<LoadingUserHeader
					onChange={(v: 'edit' | 'signatures') => handleTabChange(v)}
					currentTab={currentTab}
					searchParams={searchParams}
				/>
			) : null}
			{(DynamicContext && DynamicContext._currentValue) || userId ? (
				<UserProfileBody
					theme={theme}
					hasLoadedAttestations={hasLoadedAttestations}
					setHasLoadedAttestations={setHasLoadedAttestations}
					attestations={attestations}
					setAttestations={setAttestations}
					currentTab={currentTab}
					currentTabOption={currentTabOption}
					setCurrentTabOption={handleTabOptionChange}
					signParams={signParams}
					setSignParams={setSignParams}
					atomicLoreUrl={atomicLoreUrl}
					setEntryToDelete={setEntryToDelete}
					refreshList={refreshList}
					setRefreshList={setRefreshList}
				/>
			) : null}
			{signParams.open && (
				<SignModal
					open={signParams.open}
					onClose={() => {
						setSignParams({ open: false })
						setRefreshList(true)
					}}
					template={signParams.template}
					pendingSign={signParams.pendingSign}
					countersignEntry={signParams.countersignEntry}
					matchingAddress={signParams.matchingAddress}
					isValidSign={signParams.isValidSign}
					nft={signParams.nft}
					collection={signParams.collection}
					nftCollectionInfo={signParams.nftCollectionInfo}
					contractAddress={signParams.contractAddress}
					tokenId={signParams.tokenId}
					origin={signParams.origin}
					atomicLoreUrl={signParams.atomicLoreUrl}
					targetWallet={signParams.targetWallet}
				/>
			)}
			<DeleteModal
				open={entryToDelete != null}
				onClose={result => {
					if (result.success) {
						setEntryToDelete(null)
						if (result.message) setRefreshList(true)
					}
					if (result.message) {
						toast.custom(
							t => (
								<Toast
									variant={result.success ? 'success' : 'error'}
									//@ts-ignore
									content={result.message}
									onClose={() => toast.dismiss(t)}
								/>
							),
							{ position: 'bottom-right' },
						)
					}
				}}
				entryToDelete={entryToDelete}
			/>
		</div>
	)
}

const UserProfileHeader = ({
	currentTab,
	onChange,
	theme,
	searchParams,
}: {
	currentTab: 'edit' | 'signatures'
	onChange: (val: 'edit' | 'signatures') => void
	theme: Theme | undefined
	searchParams: URLSearchParams
}) => {
	const [defaultTab, setDefaultTab] = useState(0)

	useEffect(() => {
		if (currentTab === 'signatures') {
			setDefaultTab(1)
		} else {
			setDefaultTab(0)
		}
	}, [currentTab])
	const { user, setShowAuthFlow } = useDynamicContext()
	const isAuthenticated = useIsLoggedIn()
	const inferredUserId = user && user.userId ? user.userId : undefined

	return (
		<div className="mb-8 flex w-full flex-col justify-between md:mb-16 md:flex-row">
			<div className="mb-4 flex flex-col md:mb-0">
				<TypographyHeading
					size="large"
					isBold={true}
					additionalClasses="text-textPrimaryLight dark:text-textPrimaryDark w-[304px] mr-[66px] lg:mr-[127px]"
				>
					{isAuthenticated ? 'Manage profile' : 'Sign in to Manage Profile'}
				</TypographyHeading>
			</div>
			{!inferredUserId && (
				<Button
					variant="secondary"
					disabled={false}
					onClick={() => setShowAuthFlow(true)}
				>
					Sign In
				</Button>
			)}
			{inferredUserId && isAuthenticated && (
				<Tabs
					tabType="DefaultLargeBordered"
					defaultTab={defaultTab}
					tabOptions={[{ title: 'Edit Profile' }, { title: 'Signatures' }]}
					onChange={(v: any) => {
						if (v === 0) {
							onChange('edit')
						} else {
							onChange('signatures')
						}
					}}
				/>
			)}
		</div>
	)
}

const LoadingUserHeader = ({
	onChange,
	currentTab,
	searchParams,
}: {
	onChange: (val: 'edit' | 'signatures') => void
	currentTab: string
	searchParams: URLSearchParams
}) => {
	return (
		<div className="mb-8 flex w-full flex-col justify-between md:mb-16 md:flex-row">
			<div className="mb-4 flex flex-col md:mb-0">
				<TypographyHeading
					size="large"
					isBold={true}
					additionalClasses="text-textPrimaryLight dark:text-textPrimaryDark w-[304px] mr-[66px] lg:mr-[127px]"
				>
					Manage Profile
				</TypographyHeading>
			</div>
			<Tabs
				tabType="DefaultLargeBordered"
				defaultTab={currentTab === 'edit' ? 0 : 1}
				tabOptions={[
					{
						title: 'Edit Profile',
					},
					{
						title: 'Signatures',
					},
				]}
				onChange={(v: any) => {
					if (v === 0) {
						onChange('edit')
					} else {
						onChange('signatures')
					}
				}}
			/>
		</div>
	)
}

const UserProfileBody = ({
	theme,
	hasLoadedAttestations,
	setHasLoadedAttestations,
	attestations,
	setAttestations,
	currentTab,
	currentTabOption,
	setCurrentTabOption,
	signParams,
	setSignParams,
	atomicLoreUrl,
	setEntryToDelete,
	refreshList,
	setRefreshList,
}: {
	theme: Theme
	hasLoadedAttestations: boolean
	setHasLoadedAttestations: (v: boolean) => void
	currentTab: 'edit' | 'signatures' | undefined
	signParams: SignParamsType
	setSignParams: (params: SignParamsType) => void
	currentTabOption: 'completed' | 'pending' | 'drafts' | undefined
	setCurrentTabOption: (
		v: 'completed' | 'pending' | 'drafts' | undefined,
	) => void
	atomicLoreUrl?: string
	setEntryToDelete: (entry: PinnedMetadataProperties | null) => void
	refreshList?: boolean
	setRefreshList?: (newVal: boolean) => void
}) => {
	if (currentTab === 'signatures') {
		if (DynamicContext && DynamicContext._currentValue) {
			return (
				<UserProfileSignatureBody
					hasLoadedAttestations={hasLoadedAttestations}
					setHasLoadedAttestations={setHasLoadedAttestations}
					passedAttestations={attestations}
					setPassedAttestations={setAttestations}
					signParams={signParams}
					currentTabOption={currentTabOption}
					setCurrentTabOption={setCurrentTabOption}
					setSignParams={setSignParams}
					atomicLoreUrl={atomicLoreUrl}
					setEntryToDelete={setEntryToDelete}
					refreshList={refreshList}
					setRefreshList={setRefreshList}
				/>
			)
		} else {
			return (
				<div className="my-8 flex w-full flex-col md:my-16">
					<div className="flex justify-between">
						<TypographyHeading
							size="medium"
							isBold={true}
							additionalClasses="text-textPrimaryLight dark:text-textPrimaryDark"
						>
							All Signatures
						</TypographyHeading>
						{/* <Button size="small" onClick={() => setSignParams({ open: true, template: "collection", onClose: () => { setSignParams({ open: false }) } })}>Add Collection</Button> */}
					</div>
					<div className="mt-8 w-full justify-between border-b border-textPrimaryDark pb-4 dark:border-textPrimaryLight md:flex md:flex-row">
						<div className="flex flex-1 items-center md:gap-x-4">
							<div className="flex flex-row items-center gap-x-1">
								<PillTab
									title="Completed"
									selected={currentTabOption === 'completed' ? true : false}
									disabled={true}
								/>
							</div>
							<div className="flex flex-row items-center">
								<PillTab
									title="Pending"
									rightIcon={<Counter size="small" children={'...'} />}
									selected={currentTabOption === 'pending' ? true : false}
									disabled={true}
								/>
							</div>
							<div className="flex flex-row items-center">
								<PillTab
									title="Drafts"
									rightIcon={<Counter size="small" children={'...'} />}
									selected={currentTabOption === 'drafts' ? true : false}
									disabled={true}
								/>
							</div>
						</div>
						<div className="mt-3 flex flex-1 items-center justify-end gap-x-4 md:mt-0">
							<Filter
								type="Filter"
								options={[{ title: 'loading', description: 'loading' }]}
							></Filter>
							<Filter
								type="Sort"
								options={[{ title: 'loading', description: 'loading' }]}
							></Filter>
						</div>
					</div>
					<div className="pt-6">
						<div className="mx-auto mt-8 flex w-full flex-col items-center justify-center text-dark dark:text-light">
							<TypographyParagraph
								size="medium"
								additionalClasses="text-textSecondaryLight dark:text-textSecondaryDark"
							>
								Loading your attestations...
							</TypographyParagraph>
							<Loader size="medium" />
						</div>
					</div>
				</div>
			)
		}
	} else if (currentTab === 'edit') {
		if (DynamicContext && DynamicContext._currentValue) {
			return <UserProfileEditBody atomicLoreUrl={atomicLoreUrl} />
		} else {
			return (
				<div className="flex flex-col justify-between md:flex-row">
					<div className="mr-[66px] flex w-[304px] flex-col lg:mr-[127px]">
						<TypographyHeading
							size="medium"
							isBold={true}
							additionalClasses="text-textPrimaryLight dark:text-textPrimaryDark w-[304px]"
						>
							Personal Information
						</TypographyHeading>
						{/* <Button size="small" onClick={() => setSignParams({ open: true, template: "collection", onClose: () => { setSignParams({ open: false }) } })}>Add Collection</Button> */}
						<div className="mt-4 w-full justify-between border-b border-textPrimaryDark pb-4 dark:border-textPrimaryLight md:flex md:flex-row">
							<div className="flex justify-between">
								<TypographyParagraph
									size="medium"
									isBold={false}
									additionalClasses="text-textPrimaryLight dark:text-textPrimaryDark"
								>
									Update your avatar and username to personalize your profile.
								</TypographyParagraph>
								{/* <Button size="small" onClick={() => setSignParams({ open: true, template: "collection", onClose: () => { setSignParams({ open: false }) } })}>Add Collection</Button> */}
							</div>
						</div>
					</div>
					<div className="flex w-full flex-col">
						<div className="w-80">
							<label className="block leading-6 text-grey400 dark:text-grey500">
								<TypographyCaps size="small">Avatar</TypographyCaps>
							</label>

							<div className="group relative mt-2">
								<div className="container">
									<div>
										<div className="relative cursor-pointer group-hover:brightness-50">
											<Skeleton
												baseColor={
													theme === 'light' || theme === undefined
														? '#f5f5f5'
														: '#383838'
												}
												highlightColor={
													theme === 'light' || theme === undefined
														? '#f8f8f8'
														: '#212121'
												}
												height={'104px'}
												width={'104px'}
												className="'border-textInactiveLight rounded-lg border group-hover:border-textPrimaryLight dark:border-textInactiveDark group-hover:dark:border-textPrimaryDark"
												containerClassName="relative h-full w-full"
											/>
										</div>
									</div>
								</div>
							</div>
							<TypographyParagraph
								size="xSmall"
								additionalClasses="mt-2 text-textSecondaryLight dark:text-textSecondaryDark"
							>
								Recommended size: 400px x 400px. JPG, PNG, GIF
							</TypographyParagraph>
						</div>
						<InputField
							containerWidth="mt-6 mb-[60px]"
							// value={userName}
							// onChange={e => handleUsernameChange(e.target.value)}
							name="Username"
							placeholder="Enter Username Here"
							// helper={usernameError ? usernameError : "Alphanumeric, 2-20 characters"}
							//  !== "" ? usernameError : "Alphanumeric, 2-20 characters"}
							// isError={userName.length > 19 || usernameError !== ''}
						/>

						<Button
							variant="primary"
							// onClick={async () => await updateUserProfile()}
							size="large"
							disabled={true}
						>
							Loading
						</Button>
					</div>
				</div>
			)
		}
	} else {
		return null
	}
}

export const Attestations = ({
	completedAttestations,
	totalCompletedAttestations,
	pendingAttestations,
	totalPendingAttestations,
	draftAttestations,
	totalDraftAttestations,
	signParams,
	currentTabOption,
	setCurrentTabOption,
	setSignParams,
	setEntryToDelete,
	atomicLoreUrl,
	refreshList,
	setRefreshList,
}: {
	completedAttestations: AttestationType[]
	totalCompletedAttestations: number
	pendingAttestations: AttestationType[]
	totalPendingAttestations: number
	draftAttestations: AttestationType[]
	totalDraftAttestations: number
	signParams: SignParamsType
	setSignParams: (params: SignParamsType) => void
	currentTabOption: 'completed' | 'pending' | 'drafts' | undefined
	setCurrentTabOption: (
		v: 'completed' | 'pending' | 'drafts' | undefined,
	) => void
	atomicLoreUrl: string
	setEntryToDelete: (entry: PinnedMetadataProperties | null) => void
	refreshList?: boolean
	setRefreshList?: (newVal: boolean) => void
}) => {
	const { primaryWallet } = useDynamicContext()

	const navigate = useNavigate()

	const returnAttestationListForPassedOption = (
		passedOption: 'completed' | 'pending' | 'drafts' | undefined,
	) => {
		if (passedOption === 'pending') {
			return {
				attestationType: 'pendingAttestations',
				page: 0,
				attestations: pendingAttestations ? pendingAttestations : [],
				totalAttestations: totalPendingAttestations,
			}
		} else if (passedOption === 'drafts') {
			return {
				attestationType: 'draftAttestations',
				page: 0,
				attestations: draftAttestations ? draftAttestations : [],
				totalAttestations: totalDraftAttestations,
			}
		} else {
			return {
				attestationType: 'completedAttestations',
				page: 0,
				attestations: completedAttestations ? completedAttestations : [],
				totalAttestations: totalCompletedAttestations,
			}
		}
	}

	const [attestationsList, setAttestationsList] = useState<{
		attestationType: string
		page: number
		attestations: AttestationType[]
		totalAttestations: number | null
	}>(returnAttestationListForPassedOption(currentTabOption))

	const [sortCriteria, setSortCriteria] = useState({
		completedAttestations: { title: 'Newest', description: 'DESC' },
		pendingAttestations: { title: 'Newest', description: 'DESC' },
		draftAttestations: { title: 'Newest', description: 'DESC' },
	})
	const [filterCriteria, setFilterCriteria] = useState({
		completedAttestations: { title: 'All Types', description: '' },
		pendingAttestations: { title: 'All Types', description: '' },
		draftAttestations: { title: 'All Types', description: '' },
	})

	const [loading, setLoading] = useState(false)

	useEffect(() => {
		attestationsList.page == 0
			? loadAttestations()
			: setAttestationsList({ ...attestationsList, page: 0 })
	}, [sortCriteria, filterCriteria])

	useEffect(() => {
		if (completedAttestations != undefined) loadAttestations()
	}, [attestationsList.attestationType, attestationsList.page])

	useEffect(() => {
		if (
			attestationsList.totalAttestations === 0 &&
			totalCompletedAttestations > 0 &&
			attestationsList.attestationType === 'completedAttestations'
		) {
			setAttestationsList({
				attestationType: 'completedAttestations',
				page: 1,
				attestations: completedAttestations,
				totalAttestations: totalCompletedAttestations,
			})
		}
	}, [totalCompletedAttestations])

	useEffect(() => {
		if (refreshList) {
			if (setRefreshList) setRefreshList(false)
			loadAttestations()
		}
	}, [refreshList])

	const loadAttestations = async () => {
		if (loading) return
		setLoading(true)
		try {
			const options = {
				method: 'GET',
				headers: { 'Content-Type': 'application/json' },
			}
			const response = await fetch(
				`/userAttestationsAPI?attestationType=${attestationsList.attestationType}&includeRevoked=true&page=${attestationsList.page}&sortOrder=${sortCriteria[attestationsList.attestationType].description}${filterCriteria[attestationsList.attestationType].description.length ? `&filterCriteria=${filterCriteria[attestationsList.attestationType].description}` : ''}`,
				options,
			)
			const attestationsResponse = (await response.json()) as {
				attestationType: string
				page: number
				attestations: AttestationType[]
				count: number
			}
			if (
				attestationsList.page == attestationsResponse.page &&
				attestationsList.attestationType == attestationsResponse.attestationType
			)
				setAttestationsList({
					attestationType: attestationsList.attestationType,
					page: attestationsList.page,
					totalAttestations: attestationsResponse.count,
					attestations:
						attestationsList.page == 0
							? attestationsResponse.attestations
							: [
									...attestationsList.attestations,
									...attestationsResponse.attestations,
								],
				})
			setLoading(false)
		} catch (e) {
			console.error('Load Attestations Error', e)
		}
	}

	const options = [
		{ title: 'All Types', description: '' },
		{ title: 'Proof of exhibition', description: 'proofOfExhibition' },
		{ title: 'Comment', description: 'comment' },
		{ title: 'Media Citation', description: 'mediaCitation' },
		{ title: 'Media Upload', description: 'media' },
		{ title: 'Tweet', description: 'tweet' },
		{ title: 'Cast', description: 'cast' },
		// { title: 'Instagram', description: 'instagram' },
		{ title: 'Offchain lend', description: 'offchainLend' },
		{ title: 'Offchain sale', description: 'offchainSale' },
		{ title: 'Collection', description: 'collection' },
		{ title: 'Countersign', description: 'countersign' },
		{ title: 'Revoke', description: 'revoke' },
		{ title: 'Rollup', description: 'rollup' },
	]
	const sort = [
		{ title: 'Newest', description: 'DESC' },
		{ title: 'Oldest', description: 'ASC' },
	]

	function getTokenId(metadataEntry: AttestationType) {
		const { entry } = metadataEntry.references
		if (entry) {
			if (entry.nft && entry.nft.tokenId) {
				return entry.nft.tokenId
			} else if (entry.nft && entry.nft.tokenAddress) {
				return entry.nft.tokenAddress
			} else {
				return ''
			}
		} else {
			return ''
		}
	}

	function getContractAddress(metadataEntry: AttestationType) {
		const { entry } = metadataEntry.references
		if (entry) {
			if (entry.nft && entry.nft.contractAddress) {
				return entry.nft.contractAddress
			} else if (entry.nft && entry.nft.tokenProgram) {
				return entry.nft.tokenProgram
			} else {
				return ''
			}
		} else {
			return ''
		}
	}

	const onPendingSign = (metadataEntry: PinnedMetadataProperties) => {
		if (metadataEntry.template.slug === 'collection') {
			window.open(
				`${window.location.origin}/collectionEdit/${metadataEntry.attestationUid}`,
			)
		} else {
			setSignParams({
				open: true,
				template: metadataEntry.template.slug,
				pendingSign: metadataEntry,
				countersignEntry: metadataEntry.originalReference
					? metadataEntry.originalReference
					: undefined,
				matchingAddress: primaryWallet.address,
				isValidSign: true,
				...(metadataEntry.references.entry &&
					metadataEntry.references.entry.chain && {
						nft: {
							chain: metadataEntry.references.entry?.chain.name,
							contract: {
								address: getContractAddress(metadataEntry),
							},
							tokenId: getTokenId(metadataEntry),
							name: metadataEntry.nftName,
							nftCollection: metadataEntry.nftCollection,
							previewImage: metadataEntry.nftImage,
						},
					}),
				collection: metadataEntry.collection,
				nftCollectionInfo: metadataEntry.nftCollection,
				origin: origin,
				atomicLoreUrl: atomicLoreUrl,
			})
		}
	}

	const onDelete = async (metadataEntry: PinnedMetadataProperties) => {
		setEntryToDelete(metadataEntry)
	}

	const onCountersign = (metadataEntry: AttestationType) => {
		const { properties } = metadataEntry
		let countersigningAddress: string | undefined
		if (properties?.artistInitiated && properties?.receiverAddress) {
			countersigningAddress = properties.receiverAddress
		} else if (properties?.ownerAddress) {
			countersigningAddress = properties.ownerAddress
		}

		setSignParams({
			open: true,
			template: 'countersign',
			countersignEntry: metadataEntry,
			matchingAddress:
				primaryWallet && primaryWallet.address && countersigningAddress
					? primaryWallet.address.toLowerCase() ===
						countersigningAddress.toLowerCase()
					: false,
			isValidSign: true,
			...(metadataEntry.references.entry?.chain && {
				nft: {
					chain: metadataEntry.references.entry?.chain?.name,
					contract: {
						address: getContractAddress(metadataEntry),
					},
					tokenId: getTokenId(metadataEntry),
					name: metadataEntry.nftName,
					nftCollection: getNFTCollectionInfo(metadataEntry.nft),
					previewImage: metadataEntry.nftImage,
				},
			}),
			...(metadataEntry.collection && {
				collection: metadataEntry.collection,
			}),
			nftCollectionInfo: metadataEntry.nftCollection,
			origin: origin,
			atomicLoreUrl: atomicLoreUrl,
		})
	}

	const onRevoke = (metadataEntry: AttestationType) => {
		if (metadataEntry.template.slug === 'rollup') {
			setSignParams({
				open: true,
				template: 'revoke',
				countersignEntry: metadataEntry,
				matchingAddress:
					primaryWallet && primaryWallet.address
						? primaryWallet.address.toLowerCase() ===
							metadataEntry.address.toLowerCase()
						: false,
				isValidSign: true,
				origin: origin,
				atomicLoreUrl: atomicLoreUrl,
			})
		} else {
			let nft = undefined
			let targetWallet = undefined
			if (metadataEntry.references.targetWallet) {
				targetWallet = {
					address: metadataEntry.references.targetWallet.address,
					chain: metadataEntry.references.targetWallet.chain,
				}
			} else if (metadataEntry.references.entry?.targetWallet) {
				targetWallet = {
					address: metadataEntry.references.entry.targetWallet.address,
					chain: metadataEntry.references.entry.targetWallet.chain,
				}
			} else {
				nft = {
					chain: metadataEntry.references.entry?.chain?.name,
					contract: {
						address: getContractAddress(metadataEntry),
					},
					tokenId: getTokenId(metadataEntry),
					name: metadataEntry.nftName,
					nftCollection: getNFTCollectionInfo(metadataEntry.nft),
					previewImage: metadataEntry.nftImage,
				}
			}
			setSignParams({
				open: true,
				template: 'revoke',
				countersignEntry: metadataEntry,
				matchingAddress:
					primaryWallet && primaryWallet.address
						? primaryWallet.address.toLowerCase() ===
							metadataEntry.address.toLowerCase()
						: false,
				isValidSign: true,
				nft,
				targetWallet,
				collection: metadataEntry.collection,
				nftCollectionInfo: metadataEntry.nftCollection,
				origin: origin,
				atomicLoreUrl: atomicLoreUrl,
			})
		}
	}

	const canFetchNextPage = () => {
		return (
			attestationsList.totalAttestations == null ||
			attestationsList.totalAttestations > attestationsList.attestations.length
		)
	}

	return (
		<div className="my-8 flex w-full flex-col sm:my-16">
			<div className="flex justify-between">
				<TypographyHeading
					size="medium"
					isBold={true}
					additionalClasses="text-textPrimaryLight dark:text-textPrimaryDark"
				>
					All Signatures
				</TypographyHeading>
				<div className="flex items-center justify-end">
					<Button
						variant="primary"
						leftIcon={<PlusIcon />}
						additionalClasses="w-full mt-4"
						onClick={() =>
							setSignParams({
								open: true,
								template: 'comment',
								onClose: () => {
									setSignParams({ open: false })
								},
								targetWallet: primaryWallet
									? {
											address: primaryWallet.address,
											chain: primaryWallet.chain,
										}
									: null,
								atomicLoreUrl: atomicLoreUrl,
							})
						}
					>
						Wallet Record
					</Button>
				</div>
				{/* <Button size="small" onClick={() => setSignParams({ open: true, template: "collection", onClose: () => { setSignParams({ open: false }) } })}>Add Collection</Button> */}
			</div>
			<div className="mt-8 w-full justify-between border-b border-textPrimaryDark pb-4 dark:border-textPrimaryLight sm:flex sm:flex-row">
				<div className="flex flex-1 items-center sm:gap-x-4">
					<div className="flex flex-row items-center gap-x-1">
						<PillTab
							title="Completed"
							selected={
								attestationsList.attestationType == 'completedAttestations'
							}
							disabled={loading}
							onClick={() => {
								if (!loading) {
									setCurrentTabOption('completed')
									setAttestationsList({
										attestationType: 'completedAttestations',
										page: 0,
										attestations: [],
										totalAttestations: totalCompletedAttestations,
									})
								}
							}}
						/>
					</div>
					<div className="flex flex-row items-center">
						<PillTab
							title="Pending"
							disabled={loading}
							rightIcon={
								totalPendingAttestations !== undefined ? (
									<Counter size="small" children={totalPendingAttestations} />
								) : (
									<Counter size="small" children={0} />
								)
							}
							selected={
								attestationsList.attestationType == 'pendingAttestations'
							}
							onClick={() => {
								if (!loading) {
									setCurrentTabOption('pending')
									setAttestationsList({
										attestationType: 'pendingAttestations',
										page: 0,
										attestations: [],
										totalAttestations: totalPendingAttestations,
									})
								}
							}}
						/>
					</div>
					<div className="flex flex-row items-center">
						<PillTab
							title="Drafts"
							disabled={loading}
							rightIcon={
								totalDraftAttestations !== undefined ? (
									<Counter size="small" children={totalDraftAttestations} />
								) : (
									<Counter size="small" children={0} />
								)
							}
							selected={attestationsList.attestationType == 'draftAttestations'}
							onClick={() => {
								if (!loading) {
									setCurrentTabOption('drafts')
									setAttestationsList({
										attestationType: 'draftAttestations',
										page: 0,
										attestations: [],
										totalAttestations: totalDraftAttestations,
									})
								}
							}}
						/>
					</div>
				</div>
				<div className="mt-3 flex flex-1 items-center justify-end gap-x-4 sm:mt-0">
					<Filter
						type="Filter"
						options={options}
						value={filterCriteria[attestationsList.attestationType]}
						onChange={newVal => {
							if (!loading)
								setFilterCriteria({
									...filterCriteria,
									[attestationsList.attestationType]: newVal,
								})
						}}
					></Filter>
					<Filter
						type="Sort"
						options={sort}
						value={sortCriteria[attestationsList.attestationType]}
						onChange={newVal => {
							if (!loading)
								setSortCriteria({
									...sortCriteria,
									[attestationsList.attestationType]: newVal,
								})
						}}
					></Filter>
				</div>
			</div>
			<div className="pt-6">
				{(attestationsList.attestations &&
					attestationsList.attestations.length) ||
				loading ? (
					<>
						{!attestationsList.attestations ||
						(!attestationsList.attestations.length &&
							attestationsList.page !== 0) ||
						!loading ||
						attestationsList.page != 0 ? (
							<AttestationsTable
								data={attestationsList.attestations}
								walletAddress={primaryWallet ? primaryWallet.address : null}
								onPendingSign={onPendingSign}
								onDelete={onDelete}
								onCountersign={onCountersign}
								onRevoke={onRevoke}
								showButton={false}
								atomicLoreUrl={atomicLoreUrl}
								displayCountersignType={
									attestationsList.attestationType == 'pendingAttestations'
								}
							/>
						) : (
							<div className="mx-auto mt-8 flex w-full flex-col items-center justify-center text-dark dark:text-light">
								<TypographyParagraph
									size="medium"
									additionalClasses="text-textSecondaryLight dark:text-textSecondaryDark"
								>
									Loading your attestations...
								</TypographyParagraph>
								<Loader size="medium" />
							</div>
						)}
						{canFetchNextPage() && (
							<div className="mt-6 flex w-full items-center justify-center">
								<Tag
									showIcon={false}
									disabled={loading}
									onClick={() =>
										!loading &&
										setAttestationsList({
											...attestationsList,
											page: attestationsList.page + 1,
										})
									}
								>
									<MoreIcon />
								</Tag>
							</div>
						)}
					</>
				) : (
					<div className="flex w-full flex-col items-center justify-center">
						<TypographyParagraph
							size="medium"
							additionalClasses="text-textSecondaryLight dark:text-textSecondaryDark"
						>
							You dont' have signatures yet.
						</TypographyParagraph>
						<TypographyParagraph
							size="medium"
							additionalClasses="text-textSecondaryLight dark:text-textSecondaryDark"
						>
							All of your attestations signed will be displayed in this page.
						</TypographyParagraph>
					</div>
				)}
			</div>
		</div>
	)
}

const UserProfileSignatureBody = ({
	hasLoadedAttestations,
	setHasLoadedAttestations,
	passedAttestations,
	setPassedAttestations,
	currentTabOption,
	setCurrentTabOption,
	signParams,
	setSignParams,
	atomicLoreUrl,
	setEntryToDelete,
	refreshList,
	setRefreshList,
}: {
	passedAttestations: {
		completedAttestations: AttestationType[]
		totalCompletedAttestations: number
		pendingAttestations: AttestationType[]
		totalPendingAttestations: number
		draftAttestations: AttestationType[]
		totalDraftAttestations: number
	}
	setPassedAttestations: (attestations: {
		completedAttestations: AttestationType[]
		totalCompletedAttestations: number
		pendingAttestations: AttestationType[]
		totalPendingAttestations: number
		draftAttestations: AttestationType[]
		totalDraftAttestations: number
	}) => void
	hasLoadedAttestations: boolean
	setHasLoadedAttestations: (v: boolean) => void
	signParams: SignParamsType
	setSignParams: (params: SignParamsType) => void
	currentTabOption: 'completed' | 'pending' | 'drafts' | undefined
	setCurrentTabOption: (
		v: 'completed' | 'pending' | 'drafts' | undefined,
	) => void
	atomicLoreUrl?: string
	setEntryToDelete: (entry: PinnedMetadataProperties | null) => void
	refreshList?: boolean
	setRefreshList?: (newVal: boolean) => void
}) => {
	const isAuthenticated = useIsLoggedIn()
	const [loading, setLoading] = useState(hasLoadedAttestations ? false : true)

	useEffect(() => {
		if (!hasLoadedAttestations && isAuthenticated) {
			loadAttestations()
		}
	}, [isAuthenticated])

	const loadAttestations = async () => {
		try {
			const options = {
				method: 'GET',
				headers: { 'Content-Type': 'application/json' },
			}
			const response = await fetch('/userProfile/signaturesAPI', options)

			const attestationResponse = (await response.json()) as {
				attestations: {
					completedAttestations: AttestationType[]
					totalCompletedAttestations: number
					pendingAttestations: AttestationType[]
					totalPendingAttestations: number
					draftAttestations: AttestationType[]
					totalDraftAttestations: number
				}
			}
			setPassedAttestations(attestationResponse.attestations)
			setHasLoadedAttestations(true)
			setLoading(false)
		} catch (e) {
			console.error(e)
		}
	}

	if (isAuthenticated && passedAttestations) {
		return (
			<Attestations
				completedAttestations={passedAttestations.completedAttestations}
				totalCompletedAttestations={
					passedAttestations.totalCompletedAttestations
				}
				pendingAttestations={passedAttestations.pendingAttestations}
				totalPendingAttestations={passedAttestations.totalPendingAttestations}
				draftAttestations={passedAttestations.draftAttestations}
				totalDraftAttestations={passedAttestations.totalDraftAttestations}
				signParams={signParams}
				currentTabOption={currentTabOption}
				setCurrentTabOption={setCurrentTabOption}
				setSignParams={setSignParams}
				atomicLoreUrl={atomicLoreUrl}
				setEntryToDelete={setEntryToDelete}
				passedLoading={loading}
				refreshList={refreshList}
				setRefreshList={setRefreshList}
			/>
		)
	} else {
		return null
	}
}

const UserProfileEditBody = ({ atomicLoreUrl }: { atomicLoreUrl: string }) => {
	const { user } = useDynamicContext()
	const refresh = useRefreshUser()
	const isAuthenticated = useIsLoggedIn()
	const fetcher = useFetcher()
	const [userState, setUserState] = useState(user)
	const [imageError, setImageError] = useState('')
	const [hasChanged, setHasChanged] = useState(false)
	const [hasUpdated, setHasUpdated] = useState(false)
	const [loading, setLoading] = useState(false)

	const { identifyUser } = useAnalytics()

	const tooltipCopyPublicProfileURL = useRef<TooltipRefProps>(null)

	const handleChange = (name: string, value: string) => {
		let newUserData = { ...userState }
		const keys = name.split('.')
		let pointer: any = newUserData
		for (let i = 0; i < keys.length - 1; i++) {
			const key = keys[i]
			if (!(key in pointer)) pointer[key] = {}
			pointer = pointer[key]
		}
		pointer[keys[keys.length - 1]] = value
		setUserState(newUserData)
		setHasChanged(true)
		setHasUpdated(false)
	}

	const handleImageChange = async (value: string) => {
		if (imageError !== '') setImageError('')
		if (value) {
			toast.custom(
				t => (
					<Toast
						variant={'uploading'}
						//@ts-ignore
						content={'Uploading your new user image...'}
						onClose={() => toast.dismiss(t)}
					/>
				),
				{ position: 'bottom-right' },
			)
			try {
				if (user) {
					const formData = new FormData()
					formData.append('file', value)
					await fetcher.submit(formData, {
						action: '/imageUploadAPI',
						method: 'POST',
						encType: 'multipart/form-data',
					})
				}
			} catch (e) {
				console.error(e)
				setImageError(`Error Uploading Image: ${e}`)
				toast.custom(
					t => (
						<Toast
							variant={'error'}
							//@ts-ignore
							content={`Error Uploading Image: ${e}`}
							onClose={() => toast.dismiss(t)}
						/>
					),
					{ position: 'bottom-right' },
				)
			}
		}
	}

	const validateUsername = (username: string) => {
		if (username) {
			if (username.length > 20) {
				return 'Username is too long'
			} else if (username.length < 3) {
				return 'Username is too short'
			} else if (/\s/.test(username)) {
				return 'Spaces are not allowed in the username'
			} else {
				return ''
			}
		} else {
			return 'Username cannot be empty'
		}
	}

	const updateUserProfile = async () => {
		setLoading(true)
		if (validateUsername(userState.username)?.length) {
			toast.custom(
				t => (
					<Toast
						variant={'error'}
						//@ts-ignore
						content={
							'Username length is not valid. Please provide a username that fits within the allowed character limit.'
						}
						onClose={() => toast.dismiss(t)}
					/>
				),
				{ position: 'bottom-right' },
			)
			setLoading(false)
			return
		}
		if (
			userState.metadata.shortDescription &&
			userState.metadata.shortDescription.length > 200
		) {
			toast.custom(
				t => (
					<Toast
						variant={'error'}
						//@ts-ignore
						content={
							'Bio length is not valid. Please provide a bio that fits within the allowed character limit.'
						}
						onClose={() => toast.dismiss(t)}
					/>
				),
				{ position: 'bottom-right' },
			)
			setLoading(false)
			return
		}
		if (
			userState.metadata.longDescription &&
			userState.metadata.longDescription.length > 550
		) {
			toast.custom(
				t => (
					<Toast
						variant={'error'}
						//@ts-ignore
						content={
							'Bio description length is not valid. Please provide a bio description that fits within the allowed character limit.'
						}
						onClose={() => toast.dismiss(t)}
					/>
				),
				{ position: 'bottom-right' },
			)
			setLoading(false)
			return
		}
		if (user) {
			try {
				const updateUserResponse = await fetch(
					`/updateUserProfile/${user.userId}`,
					{
						method: 'PATCH',
						headers: {
							'Content-Type': 'application/json',
						},
						body: JSON.stringify(userState),
					},
				)
				const jsonResponse = await updateUserResponse.json()
				const username = userState.username || user.username
				await refresh()
				setHasUpdated(true)
				setHasChanged(false)
				setLoading(false)
				toast.custom(
					t => (
						<Toast
							variant={'success'}
							//@ts-ignore
							content={"You've successfully updated your profile!"}
							onClose={() => toast.dismiss(t)}
						/>
					),
					{ position: 'bottom-right' },
				)
				if (user.userId && username) {
					try {
						await identifyUser(user.userId, username, user.email)
					} catch (e) {
						console.error('ERROR CALLING IDENTIFY')
					}
				}
			} catch (e) {
				setLoading(false)
				console.error('ERROR UPDATING USER', { e, userState })
				if (e) {
					if (
						e?.message &&
						e?.message.toLowerCase &&
						e?.message.toLowerCase().includes('avatar')
					) {
						setImageError(e.message)
					} else {
						toast.custom(
							t => (
								<Toast
									variant={'error'}
									//@ts-ignore
									content={
										'An error occured updating your profile, Please try again.'
									}
									onClose={() => toast.dismiss(t)}
								/>
							),
							{ position: 'bottom-right' },
						)
					}
				} else {
					toast.custom(
						t => (
							<Toast
								variant={'error'}
								//@ts-ignore
								content={
									'An error occured updating your profile, Please try again.'
								}
								onClose={() => toast.dismiss(t)}
							/>
						),
						{ position: 'bottom-right' },
					)
				}
			}
		} else {
			toast.custom(
				t => (
					<Toast
						variant={'error'}
						//@ts-ignore
						content={
							'An error occurred updating your profile, plese try again!'
						}
						onClose={() => toast.dismiss(t)}
					/>
				),
				{ position: 'bottom-right' },
			)
		}
	}

	useEffect(() => {
		if (fetcher.data) {
			if (fetcher.data.imageUrl) {
				if (userState.metadata?.avatar !== fetcher.data.imageUrl) {
					toast.custom(
						t => (
							<Toast
								variant={'success'}
								//@ts-ignore
								content={'Upload Successfully!'}
								onClose={() => toast.dismiss(t)}
							/>
						),
						{ position: 'bottom-right' },
					)
					handleChange('metadata.avatar', fetcher.data.imageUrl)
				}
			}
		}
	}, [fetcher])

	if (isAuthenticated) {
		return (
			<div className="flex flex-col gap-y-10">
				<div className="flex flex-col justify-between md:flex-row">
					<div className="mr-[66px] flex w-[304px] flex-col lg:mr-[127px]">
						<TypographyHeading
							size="medium"
							isBold={true}
							additionalClasses="text-textPrimaryLight dark:text-textPrimaryDark w-[304px]"
						>
							Personal Information
						</TypographyHeading>
						{/* <Button size="small" onClick={() => setSignParams({ open: true, template: "collection", onClose: () => { setSignParams({ open: false }) } })}>Add Collection</Button> */}
						<div className="mt-4 w-full justify-between border-b border-textPrimaryDark pb-4 dark:border-textPrimaryLight md:flex md:flex-row">
							<div className="flex justify-between">
								<TypographyParagraph
									size="medium"
									isBold={false}
									additionalClasses="text-textPrimaryLight dark:text-textPrimaryDark"
								>
									Update your avatar and username to personalize your profile.
								</TypographyParagraph>
								{/* <Button size="small" onClick={() => setSignParams({ open: true, template: "collection", onClose: () => { setSignParams({ open: false }) } })}>Add Collection</Button> */}
							</div>
						</div>
					</div>
					<div className="flex w-full flex-col">
						<ImageInput
							name="Avatar"
							value={
								userState.metadata.avatar
									? userState.metadata.avatar
									: FallbackAvatar
							}
							uploadDescription={
								'Recommended size: 400px x 400px.  JPG, PNG, GIF'
							}
							helper={imageError}
							isError={imageError !== ''}
							onChange={v => handleImageChange(v)}
						/>
						<InputField
							containerWidth="mt-6"
							value={userState.username}
							onChange={e => handleChange('username', e.target.value)}
							name="Username"
							placeholder="Enter Username Here"
							helper={
								validateUsername(userState.username) !== ''
									? validateUsername(userState.username)
									: 'Alphanumeric, 2-20 characters'
							}
							isError={
								userState.username.length > 20 || userState.username.length < 3
							}
						/>
						<InputField
							containerWidth="mt-6"
							value={userState.email}
							//onChange={e => handleChange("email", e.target.value)}
							name="Email"
							placeholder="Enter Email Here"
							// Email cannot be updated via API
							isInactive={true}
							helper={
								'Your email cannot be changed. Contact support if you need to modify it.'
							}
						/>
						<InputField
							containerWidth="mt-6"
							value={userState.metadata.location}
							onChange={e => handleChange('metadata.location', e.target.value)}
							name="Location"
							placeholder="Enter Location Here"
						/>
						<InputField
							containerWidth="mt-6"
							additionalClasses="field-sizing-content"
							value={userState.metadata.shortDescription}
							onChange={e =>
								handleChange('metadata.shortDescription', e.target.value)
							}
							name="Bio"
							placeholder="Enter your bio title"
							lines={2}
							autoAdjust={true}
							helper={`${userState.metadata.shortDescription ? userState.metadata.shortDescription.length : 0}/200 characters`}
							isError={
								userState.metadata.shortDescription
									? userState.metadata.shortDescription.length > 200
									: false
							}
						/>
						<InputField
							containerWidth="mt-6"
							value={userState.metadata.longDescription}
							onChange={e =>
								handleChange('metadata.longDescription', e.target.value)
							}
							name="Description"
							placeholder="Entrer your bio description"
							lines={4}
							autoAdjust={true}
							helper={`${userState.metadata.longDescription ? userState.metadata.longDescription.length : 0}/550 characters`}
							isError={
								userState.metadata.longDescription
									? userState.metadata.longDescription.length > 550
									: false
							}
						/>
					</div>
				</div>
				<div className="flex flex-col justify-between md:flex-row">
					<div className="mr-[66px] flex w-[304px] flex-col lg:mr-[127px]">
						<TypographyHeading
							size="medium"
							isBold={true}
							additionalClasses="text-textPrimaryLight dark:text-textPrimaryDark w-[304px]"
						>
							Website
						</TypographyHeading>
						<div className="mt-4 w-full justify-between border-b border-textPrimaryDark pb-4 dark:border-textPrimaryLight md:flex md:flex-row">
							<div className="flex justify-between">
								<TypographyParagraph
									size="medium"
									isBold={false}
									additionalClasses="text-textPrimaryLight dark:text-textPrimaryDark"
								>
									Add a URL to display on your profile, including http:// or
									https:// ..
								</TypographyParagraph>
							</div>
						</div>
					</div>
					<div className="flex w-full flex-col">
						<InputField
							containerWidth="w-full"
							value={
								userState.metadata.socials
									? userState.metadata.socials.website
									: ''
							}
							onChange={e =>
								handleChange('metadata.socials.website', e.target.value)
							}
							name="Website"
							placeholder="Enter Website URL Here"
						/>
					</div>
				</div>
				<div className="flex flex-col justify-between md:flex-row">
					<div className="mr-[66px] flex w-[304px] flex-col lg:mr-[127px]">
						<TypographyHeading
							size="medium"
							isBold={true}
							additionalClasses="text-textPrimaryLight dark:text-textPrimaryDark w-[304px]"
						>
							Social Media Links
						</TypographyHeading>
						<div className="mt-4 w-full justify-between border-b border-textPrimaryDark pb-4 dark:border-textPrimaryLight md:flex md:flex-row">
							<div className="flex justify-between">
								<TypographyParagraph
									size="medium"
									isBold={false}
									additionalClasses="text-textPrimaryLight dark:text-textPrimaryDark"
								>
									Add social links to be displayed on your profile, including
									http:// or https:// .
								</TypographyParagraph>
							</div>
						</div>
					</div>
					<div className="flex grid w-full grid-cols-1 flex-wrap gap-y-4 sm:!grid-cols-2">
						<InputField
							containerWidth="mr-0 sm:mr-2"
							value={
								userState.metadata.socials ? userState.metadata.socials.x : ''
							}
							onChange={e => handleChange('metadata.socials.x', e.target.value)}
							name="Twitter"
							placeholder="Enter X Profile URL Here"
						/>
						<InputField
							containerWidth="mr-0 sm:ml-2"
							value={
								userState.metadata.socials
									? userState.metadata.socials.instagram
									: ''
							}
							onChange={e =>
								handleChange('metadata.socials.instagram', e.target.value)
							}
							name="Instagram"
							placeholder="Enter Instagram Profile URL Here"
						/>
						<InputField
							containerWidth="mr-0 sm:mr-2"
							value={
								userState.metadata.socials
									? userState.metadata.socials.discord
									: ''
							}
							onChange={e =>
								handleChange('metadata.socials.discord', e.target.value)
							}
							name="Discord"
							placeholder="Enter Discord URL Here"
						/>
						<InputField
							containerWidth="mr-0 sm:ml-2"
							value={
								userState.metadata.socials
									? userState.metadata.socials.opensea
									: ''
							}
							onChange={e =>
								handleChange('metadata.socials.opensea', e.target.value)
							}
							name="OpenSea"
							placeholder="Enter OpenSea profile URL Here"
						/>
					</div>
				</div>
				<div className="flex flex-col justify-between md:flex-row">
					<div className="mr-[66px] flex w-[304px] flex-col lg:mr-[127px]">
						<TypographyHeading
							size="medium"
							isBold={true}
							additionalClasses="text-textPrimaryLight dark:text-textPrimaryDark w-[304px]"
						>
							Atomic Lore Profile URL
						</TypographyHeading>
						<div className="mt-4 w-full justify-between border-b border-textPrimaryDark pb-4 dark:border-textPrimaryLight md:flex md:flex-row">
							<div className="flex justify-between">
								<TypographyParagraph
									size="medium"
									isBold={false}
									additionalClasses="text-textPrimaryLight dark:text-textPrimaryDark"
								>
									Copy your Atomic Lore profile URL to share it, or open it in a
									new tab to check how it looks.
								</TypographyParagraph>
							</div>
						</div>
					</div>
					<div className="flex w-full flex-col">
						<div
							id="copyPublicProfileURL"
							className="mb-[60px] flex flex-row items-end gap-x-2"
						>
							<InputField
								containerWidth="w-full"
								additionalClasses="text-textSecondaryDark dark:text-textSecondaryLight bg-textPrimaryDark dark:bg-textPrimaryLight"
								value={getUserProfileURL(
									userState?.userId ? userState?.userId : userState?.username,
									atomicLoreUrl,
								)}
								name="Profile URL"
								placeholder="Your public user profile will be displayed here"
								isInactive={true}
								icon={<CopyIcon />}
								onIconClick={() => {
									navigator.clipboard.writeText(
										getUserProfileURL(
											userState?.userId
												? userState?.userId
												: userState?.username,
											atomicLoreUrl,
										),
									)
									tooltipCopyPublicProfileURL.current?.open({
										anchorSelect: '#copyPublicProfileURL',
										content: 'Copied!',
									})
									setTimeout(() => {
										tooltipCopyPublicProfileURL.current?.close()
									}, 2000)
								}}
							/>
							<InternalLink
								to={`/userProfile/${user?.userId}`}
								target={'_blank'}
							>
								<Button
									leftIcon={<OpenInNewIcon />}
									variant="secondary"
									additionalClasses="gap-x-0 hover:border hover:border-transparent border-textInactiveDark dark:border-textInactiveDark px-3 py-[23px] items-center justify-center"
								/>
							</InternalLink>
						</div>
						<Tooltip imperativeModeOnly ref={tooltipCopyPublicProfileURL} />
						<Button
							variant="primary"
							onClick={async () => await updateUserProfile()}
							size="large"
							disabled={!hasChanged || loading}
						>
							{loading
								? 'Saving changes...'
								: hasUpdated
									? 'Saved'
									: 'Save Changes'}
						</Button>
					</div>
				</div>
			</div>
		)
	} else {
		return null
	}
}

export function ErrorBoundary() {
	const error = useRouteError()
	captureRemixErrorBoundaryError(error)

	if (isRouteErrorResponse(error)) {
		console.error('Error is:', error)
		return (
			<div title={'Error Loading Attestation'}>
				<TypographyParagraph size="large">
					An error occurred while loading the User Profile Page
				</TypographyParagraph>
				<TypographyParagraph size="large" additionalClasses="pt-2 pb-4">
					Error Status: {error.status} - {error.statusText}
				</TypographyParagraph>
				<TypographyParagraph size="large" additionalClasses="pt-2 pb-4">
					Error Data: {error.data}
				</TypographyParagraph>
			</div>
		)
	} else if (error instanceof Error) {
		console.error('Error is:', error)
		return (
			<div title={'Error Loading Attestation'}>
				<TypographyParagraph size="large">
					An error occurred while loading the User Profile Page
				</TypographyParagraph>
				<TypographyParagraph size="large" additionalClasses="pt-2 pb-4">
					Error Message: {error.message}
				</TypographyParagraph>
			</div>
		)
	} else {
		return (
			<div title={'Error Loading Attestation'}>
				<TypographyParagraph size="large">
					An unknown error occurred while loading the User Profile Page
				</TypographyParagraph>
			</div>
		)
	}
}
